import { useUpdateInstrumentRunConsumables } from '@/pages/Instrument_Runs/hooks';
import type { InstrumentRunConsumables } from '@/types';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import React, { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import parentStyles from '../../styles.module.css';

import { type FieldName, READ_ONLY_FIELD_ROWS, USER_INPUT_FIELDS, toFormValues, toPatchPayload } from './helpers';
import styles from './styles.module.css';

const MAX_LENGTH = 1024;

type Props = {
  orgId: string;
  runId: string;
  values: Partial<InstrumentRunConsumables> | null | undefined;
  // Editable only while the page's single Edit-mode toggle is on — mirrors name/description.
  isEditing: boolean;
};

export type InstrumentRunConsumablesHandle = {
  // Invoked from the page's single Save action alongside the main run submit, not from a
  // dedicated button of its own — see the review thread on PR #254 this replaced.
  submit: () => void;
};

const ReadOnlyConsumableInput = ({
  label,
  id,
  value,
  isFlowCell,
}: {
  label: string;
  id: string;
  value: string | null | undefined;
  isFlowCell: boolean;
}) => (
  <Input
    classNames={{ label: parentStyles.consumableLabel, input: styles.disabledInput }}
    // Flowcell SNs keep the default faded disabled styling; the other URM fields opt into the
    // legible (opacity: 1) treatment.
    containerClassName={isFlowCell ? undefined : styles.readableDisabled}
    data-testid={id}
    disabled
    id={id}
    label={{ children: label, id: `label-${id}`, htmlFor: id }}
    size="sm"
    value={value ?? ''}
  />
);

const InstrumentRunConsumables = forwardRef<InstrumentRunConsumablesHandle, Props>(function InstrumentRunConsumables(
  { orgId, runId, values, isEditing },
  ref,
) {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  const mutation = useUpdateInstrumentRunConsumables(orgId, runId);
  const maxLengthMessage = t('cons_input_max_length', { count: MAX_LENGTH });

  const flowCellIds = (values?.three_flow_cell_ids ?? '').split(',').map(s => s.trim());

  const schema = useMemo(
    () =>
      z.object(
        USER_INPUT_FIELDS.reduce(
          (acc, { field }) => {
            acc[field] = z.string().max(MAX_LENGTH, maxLengthMessage);
            return acc;
          },
          {} as Record<FieldName, z.ZodString>,
        ),
      ),
    [maxLengthMessage],
  );

  const initialValues = useMemo(() => toFormValues(values), [values]);

  const form = useForm({
    defaultValues: initialValues,
    validators: {
      onChange: ({ value }) => {
        const result = schema.safeParse(value);
        // Form-level validators must nest per-field errors under `fields` for TanStack Form to
        // surface them on each field's own meta — a flat map is silently ignored.
        return result.success ? undefined : { fields: result.error.flatten().fieldErrors };
      },
    },
    onSubmit: async ({ value }) => {
      try {
        await mutation.mutateAsync(toPatchPayload(value, initialValues));
        toast.success(t('cons_input_save_success'));
      } catch {
        toast.error(t('cons_input_save_error'));
      }
    },
  });

  // TanStack Form only seeds `defaultValues` on mount — reset explicitly when the incoming
  // editable values actually change (post-save server normalization, background refetch), or the
  // form keeps showing stale data until a full page reload. Compare the derived editable values
  // (not the raw `values` prop) and skip the reset while the form is dirty — the route polls
  // active runs, and a URM-only field changing (e.g. flowcell IDs) shouldn't stomp in-progress
  // lot/SN text the user hasn't saved yet.
  const previousInitialValuesRef = useRef(initialValues);
  useEffect(() => {
    const editableValuesChanged = USER_INPUT_FIELDS.some(
      ({ field }) => previousInitialValuesRef.current[field] !== initialValues[field],
    );
    if (!editableValuesChanged) return;
    previousInitialValuesRef.current = initialValues;
    if (!form.state.isDirty) {
      form.reset(initialValues);
    }
  }, [initialValues, form]);

  // Fired from the page's own single Save action (RunActionRow), not a button of ours — see
  // Props.isEditing.
  useImperativeHandle(ref, () => ({ submit: () => void form.handleSubmit() }), [form]);

  return (
    <Stack className={parentStyles.consumablesContainer} fullWidth gap={3} orientation="vertical">
      <Text as="h3">{t('consumables')}</Text>

      <Grid className={parentStyles.consumablesGrid} gridTemplateColumns="repeat(4, 1fr)">
        {READ_ONLY_FIELD_ROWS.map((row, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key -- rows are a fixed, never-reordered layout
          <React.Fragment key={rowIndex}>
            {row.map(({ field, labelKey, flowCellIndex }) => {
              const inputId = `consumable-${field}${flowCellIndex !== undefined ? `-${flowCellIndex}` : ''}`;
              const value = flowCellIndex !== undefined ? flowCellIds[flowCellIndex] : values?.[field];

              return (
                <ReadOnlyConsumableInput
                  key={inputId}
                  id={inputId}
                  isFlowCell={flowCellIndex !== undefined}
                  label={t(labelKey)}
                  value={value}
                />
              );
            })}
            {/* Pad each row out to the 4-column grid so the next row's fields land on their approved slot */}
            {Array.from({ length: 4 - row.length }, (_, i) => (
              <div key={`gap-${rowIndex}-${i}`} />
            ))}
          </React.Fragment>
        ))}

        {USER_INPUT_FIELDS.map(({ field, labelKey, kind }) => {
          const inputId = `consumable-input-${field}`;
          const placeholder = kind === 'lotId' ? t('cons_input_lot_id_placeholder') : t('cons_input_sn_placeholder');

          return (
            <form.Field key={field} name={field}>
              {fieldApi => {
                const fieldErrors = fieldApi.state.meta.errors;
                const errorMessage = fieldErrors.length > 0 ? String(fieldErrors[0]) : undefined;

                return (
                  <Input
                    classNames={{
                      label: parentStyles.consumableLabel,
                      input: !isEditing ? styles.disabledInput : undefined,
                    }}
                    containerClassName={!isEditing ? styles.readableDisabled : undefined}
                    data-testid={inputId}
                    disabled={!isEditing}
                    error={errorMessage}
                    id={inputId}
                    label={{ children: t(labelKey), id: `label-${inputId}`, htmlFor: inputId }}
                    onBlur={fieldApi.handleBlur}
                    onChange={e => fieldApi.handleChange((e.target as HTMLInputElement).value)}
                    placeholder={placeholder}
                    size="sm"
                    value={fieldApi.state.value}
                  />
                );
              }}
            </form.Field>
          );
        })}
      </Grid>
    </Stack>
  );
});

export default memo(InstrumentRunConsumables);
